import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Hand } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { MONO } from './ui';

const API = process.env.REACT_APP_BACKEND_URL;

// Sends the taps once per finished attempt; the server keeps only the first attempt.
const useSubmit = (slug, clipId, finished, taps) => {
  const { token } = useAuth();
  const [res, setRes] = useState(null);
  useEffect(() => {
    if (!finished || !slug) { setRes(null); return; }
    axios.post(`${API}/api/lectures/${slug}/rcount`, { clip_id: clipId, taps }, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setRes(r.data)).catch(() => {});
  }, [finished]); // eslint-disable-line
  return res;
};

const clean = (w) => w.replace(/[.,;:!?"]/g, '');

// Match each tap to the closest earlier target word (reaction window 2s).
export const scoreTaps = (lines, taps) => {
  const keys = lines.flat().filter((w) => w[2]);
  const hit = new Set();
  taps.forEach((tp) => {
    const i = keys.findIndex((k, j) => !hit.has(j) && k[0] <= tp + 0.3 && k[0] >= tp - 2);
    if (i >= 0) hit.add(i);
  });
  return { keys, hit };
};

export const RCounter = ({ lines, taps, onTap, finished, started, onRetry, t, big, slug, clipId }) => {
  const res = useSubmit(slug, clipId, finished, taps);
  if (finished) {
    const { keys, hit } = scoreTaps(lines, taps);
    return (
      <div className="rounded-xl bg-emerald-400/10 border border-emerald-300/30 p-4" data-testid="r-counter-result">
        <p className={`${big ? 'text-3xl' : 'text-lg'} font-extrabold text-white`}>{t.rYouCounted} <span className="text-amber-300" data-testid="r-counter-user">{taps.length}</span> · {t.rRealCount} <span className="text-emerald-300" data-testid="r-counter-real">{keys.length}</span></p>
        <p className="mt-1 text-xs text-blue-100/70">{t.rCaught}: {hit.size} · {t.rMissed}: {keys.length - hit.size}</p>
        <p className="mt-3 flex flex-wrap gap-1.5">
          {keys.map((k, j) => <span key={`${k[0]}-${j}`} className={`px-2 py-0.5 rounded-md text-sm font-semibold ${hit.has(j) ? 'bg-emerald-400/20 text-emerald-200' : 'bg-rose-500/25 text-rose-200 line-through decoration-rose-300/60'}`} data-testid={hit.has(j) ? 'r-caught' : 'r-missed'}>{clean(k[1])}</span>)}
        </p>
        {res && <p className="mt-3 text-xs text-amber-200" data-testid="r-counter-saved">{res.counted ? `${t.rSavedFirst} ${res.first.score}/100` : `${t.rOnlyFirst} ${res.first.score}/100`}</p>}
        <button onClick={onRetry} className="mt-3 text-xs font-bold text-amber-300 underline" data-testid="r-counter-retry">{t.rRetry}</button>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-3">
      <button onClick={onTap} disabled={!started} className={`inline-flex items-center gap-2 rounded-full bg-amber-300 hover:bg-amber-200 active:scale-95 disabled:opacity-40 text-slate-950 font-bold whitespace-nowrap transition-transform ${big ? 'px-8 py-4 text-xl' : 'px-4 py-2 text-sm'}`} data-testid="r-counter-tap">
        <Hand className="w-4 h-4 shrink-0" />{t.rTap}
      </button>
      <span className="text-2xl font-extrabold text-white" style={MONO} data-testid="r-counter-count">{taps.length}</span>
      {!started && <span className="basis-full text-xs text-blue-100/60">{t.rHint}</span>}
    </div>
  );
};
